/* 
* <license header>
*/

import React, { useState, useEffect, useMemo } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { Logo } from './Logo'
import { APP_TYPES, APP_TEMPLATES, APPS } from '../data/constants'
import { useFavorites } from '../context/FavoritesContext'

function SideBar ({ onNavigate }) {
  const location = useLocation()
  const { isFavorite } = useFavorites()
  const [appRepoExpanded, setAppRepoExpanded] = useState(false)
  const [aboutExpanded, setAboutExpanded] = useState(false)

  const favoriteApps = useMemo(
    () => APPS.filter((app) => isFavorite(app.id)),
    [isFavorite]
  )

  useEffect(() => {
    if (location.pathname.startsWith('/apps')) {
      setAppRepoExpanded(true)
    }
  }, [location.pathname])

  useEffect(() => {
    if (location.pathname.startsWith('/about')) {
      setAboutExpanded(true)
    }
  }, [location.pathname])

  return (
    <div className="SideBar">
      <div className="SideBar-brand">
        <Logo className="SideBar-logo" />
      </div>

      <nav className="SideBar-section" aria-label="Primary navigation">
        <NavLink
          className={({ isActive }) => `SideNav-itemLink ${isActive ? 'is-selected' : ''}`}
          aria-current="page"
          end
          to="/"
          onClick={onNavigate}
        >
          Home
        </NavLink>
        <NavLink
          className={({ isActive }) => `SideNav-itemLink ${isActive ? 'is-selected' : ''}`}
          aria-current="page"
          to="/ask-assistant"
          onClick={onNavigate}
        >
          Ask Assistant
        </NavLink>
        <NavLink
          className={({ isActive }) => `SideNav-itemLink ${isActive ? 'is-selected' : ''}`}
          aria-current="page"
          to="/config"
          onClick={onNavigate}
        >
          Configuration
        </NavLink>
        <div className="SideNav-submenu">
          <div className="SideNav-parentRow">
            <NavLink
              className={() => `SideNav-itemLink SideNav-parentLink ${location.pathname.startsWith('/about') ? 'is-selected' : ''}`}
              aria-current={location.pathname === '/about' ? 'page' : undefined}
              end
              to="/about"
              onClick={onNavigate}
            >
              About
            </NavLink>
            <button
              type="button"
              className={`SideNav-chevronBtn ${aboutExpanded ? 'is-expanded' : ''}`}
              onClick={(e) => { e.preventDefault(); setAboutExpanded((v) => !v) }}
              aria-expanded={aboutExpanded}
              aria-label={aboutExpanded ? 'Collapse About submenu' : 'Expand About submenu'}
            >
              ›
            </button>
          </div>
          {aboutExpanded && (
            <div className="SideNav-submenuItems">
              <NavLink
                className={({ isActive }) => `SideNav-itemLink SideNav-subItemLink ${isActive ? 'is-selected' : ''}`}
                to="/about/roadmap"
                onClick={onNavigate}
              >
                Roadmap
              </NavLink>
            </div>
          )}
        </div>
      </nav>

      <div className="SideBar-applications">
        <h3 className="SideBar-sectionTitle">Applications</h3>
        <nav className="SideBar-section" aria-label="Secondary navigation">
        <NavLink
          className={({ isActive }) => `SideNav-itemLink ${isActive ? 'is-selected' : ''}`}
          aria-current="page"
          to="/onboard"
          onClick={onNavigate}
        >
          Onboard existing application
        </NavLink>
        <NavLink
          className={({ isActive }) => `SideNav-itemLink ${isActive ? 'is-selected' : ''}`}
          aria-current="page"
          to="/app-types"
          onClick={onNavigate}
        >
          App Builder Type Browser
        </NavLink>
        <NavLink
          className={({ isActive }) => `SideNav-itemLink ${isActive ? 'is-selected' : ''}`}
          aria-current="page"
          to="/app-templates"
          onClick={onNavigate}
        >
          App Templates
        </NavLink>
        <div className="SideNav-submenu">
          <div className="SideNav-parentRow">
            <NavLink
              className={() => `SideNav-itemLink SideNav-parentLink ${location.pathname.startsWith('/apps') ? 'is-selected' : ''}`}
              aria-current={location.pathname.startsWith('/apps') ? 'page' : undefined}
              to="/apps"
              onClick={onNavigate}
            >
              Applications
            </NavLink>
            <button
              type="button"
              className={`SideNav-chevronBtn ${appRepoExpanded ? 'is-expanded' : ''}`}
              onClick={(e) => { e.preventDefault(); setAppRepoExpanded((v) => !v) }}
              aria-expanded={appRepoExpanded}
              aria-label={appRepoExpanded ? 'Collapse submenu' : 'Expand submenu'}
            >
              ›
            </button>
          </div>
          {appRepoExpanded && (
            <div className="SideNav-submenuItems">
              {favoriteApps.map((app) => (
                <NavLink
                  key={app.id}
                  className={({ isActive }) => `SideNav-itemLink SideNav-subItemLink ${isActive ? 'is-selected' : ''}`}
                  to={`/apps/${app.id}`}
                  onClick={onNavigate}
                >
                  {app.title}
                </NavLink>
              ))}
            </div>
          )}
        </div>
        </nav>
      </div>

      <div className="SideBar-quickLinks">
        <h3 className="SideBar-sectionTitle">Quick Links</h3>
        <ul className="SideBar-linkList">
          <li>
            <a href="https://developer.adobe.com/console" target="_blank" rel="noopener noreferrer" className="SideBar-externalLink">
              Adobe Console
            </a>
          </li>
          <li>
            <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="SideBar-externalLink">
              Github
            </a>
          </li>
          <li>
            <a href="https://developer.adobe.com/app-builder" target="_blank" rel="noopener noreferrer" className="SideBar-externalLink">
              Documentation
            </a>
          </li>
        </ul>
      </div>

      <div className="SideBar-stats">
        <div className="SideBar-stat">{APPS.length} managed apps</div>
        <div className="SideBar-stat">{APP_TYPES.length} app types supported</div>
        <div className="SideBar-stat">{APP_TEMPLATES.length} templates</div>
      </div>
    </div>
  )
}

export default SideBar
